import React, { useContext, useMemo } from "react";
import {
  Card,
  Table,
  Pagination,
  Row,
  Col,
  Form,
  Container,
} from "react-bootstrap";
import { usePagination, useTable } from "react-table";
import { useTranslation } from "react-i18next";
import getWatchdogColumns from "../../../../inputData/simWatchdogData";
import { FaRedo } from "react-icons/fa";
import { resetWatchdog } from "../../../../redux/slices/watchdogs";
import NotyfContext from "../../../../contexts/NotyfContext";

const SimWatchdogTable = ({ columns, data, simId }) => {
  const { t } = useTranslation();
  const notyf = useContext(NotyfContext);
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize },
  } = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: 0 },
    },
    usePagination,
    (hooks) => {
      hooks.visibleColumns.push((columns) => [
        ...columns,
        {
          id: "actions",
          Header: t("Actions"),
          Cell: ({ row }) => {
            return (
              <div className="d-flex">
                <FaRedo
                  size={16}
                  onClick={async () => {
                    await resetWatchdog({
                      simId: simId,
                      watchdogId: row.original.watchDog.id,
                    })
                      .then(() => {
                        notyf.open({
                          type: "success",
                          message: "Watchdog reset successfully",
                          duration: 5000,
                          ripple: true,
                          dismissible: false,
                          position: {
                            x: "center",
                            y: "top",
                          },
                        });
                      })
                      .catch((error) => {
                        notyf.open({
                          type: "danger",
                          duration: 5000,
                          message: error,
                          ripple: true,
                          dismissible: false,
                          position: {
                            x: "center",
                            y: "top",
                          },
                        });
                      });
                  }}
                />
              </div>
            );
          },
        },
      ]);
    }
  );

  return (
    <Card>
      <Card.Header>
        <Card.Title>{t("simProfile.watchdog")}</Card.Title>
      </Card.Header>
      <Card.Body>
        <Table hover responsive {...getTableProps()}>
          <thead>
            {headerGroups.map((headerGroup) => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column) => (
                  <th {...column.getHeaderProps()}>
                    {column.render("Header")}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {page.map((row, i) => {
              prepareRow(row);
              return (
                <tr {...row.getRowProps()}>
                  {row.cells.map((cell) => {
                    return (
                      <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </Table>

        <Row>
          <Col md="6">
            <span className="mx-2">
              {t("table.page")}{" "}
              <strong>
                {pageIndex + 1} {t("table.of")} {pageOptions.length}
              </strong>
            </span>
            <span className="ms-3 me-2">{t("table.show")}:</span>
            <Form.Select
              className="d-inline-block w-auto"
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
              }}
            >
              {[10, 20, 30, 40, 50].map((pageSize) => (
                <option key={pageSize} value={pageSize}>
                  {pageSize}
                </option>
              ))}
            </Form.Select>

            <span className="ms-3 me-2">{t("table.goToPage")}:</span>
            <Form.Control
              className="d-inline-block"
              type="number"
              defaultValue={pageIndex + 1}
              onChange={(e) => {
                const page = e.target.value ? Number(e.target.value) - 1 : 0;
                gotoPage(page);
              }}
              style={{ width: "75px" }}
            />
          </Col>
          <Col md="6">
            <Pagination className="float-end">
              <Pagination.First
                onClick={() => gotoPage(0)}
                disabled={!canPreviousPage}
              />
              <Pagination.Prev
                onClick={() => previousPage()}
                disabled={!canPreviousPage}
              />
              <Pagination.Next
                onClick={() => nextPage()}
                disabled={!canNextPage}
              />
              <Pagination.Last
                onClick={() => gotoPage(pageCount - 1)}
                disabled={!canNextPage}
              />
            </Pagination>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
};

const SimWatchdogs = ({ sim }) => {
  const { t } = useTranslation();
  const { simWatchdogColumns } = useMemo(() => getWatchdogColumns(t), [t]);

  return (
    <Container fluid className="p-0">
      <SimWatchdogTable
        columns={simWatchdogColumns}
        data={sim.simWatchDogs}
        simId={sim.id}
      />
    </Container>
  );
};

export default SimWatchdogs;
